import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/styles";
import { Button, Container, Grid, Typography } from "@material-ui/core";
import { headRewrite } from "../function/headRewrite";
import { Helmet } from "react-helmet-async";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#eeeeee",
    color: "#111",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, "Noto Sans JP", "Yu Gothic", sans-serif',
  },
  pageFrame: {
    background: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      borderLeft: "none",
      borderRight: "none",
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(3, 0),
    borderBottom: "1px solid #111",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2, 0),
    },
  },
  logo: {
    fontWeight: 700,
    letterSpacing: "-0.04em",
    fontSize: 28,
    [theme.breakpoints.down("sm")]: {
      fontSize: 24,
    },
  },
  nav: {
    display: "flex",
    gap: theme.spacing(4),
    fontSize: 13,
    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(2),
      fontSize: 12,
    },
  },
  navLink: {
    color: "#111",
    textDecoration: "none",
  },
  section: {
    padding: theme.spacing(10, 0),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0),
    },
  },
  hero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6, 0, 7),
    },
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    fontSize: 12,
    letterSpacing: "0.12em",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  labelLine: {
    flex: 1,
    height: 1,
    background: "#111",
    opacity: 0.7,
  },
  heroTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(26px, 4.5vw, 46px)",
    lineHeight: 1.16,
    letterSpacing: "-0.08em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  heroBody: {
    maxWidth: 560,
    fontSize: 15,
    lineHeight: 2,
    color: "#222",
    marginBottom: theme.spacing(4),
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
  ctaButton: {
    minWidth: 250,
    minHeight: 52,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      minWidth: "auto",
    },
  },
  primaryButton: {
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#111",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
      boxShadow: "none",
    },
  },
  ctaNote: {
    fontSize: 13,
    lineHeight: 1.9,
    color: "#333",
    marginTop: theme.spacing(2.5),
    marginBottom: 0,
  },
  textLink: {
    color: "#111",
    textDecoration: "none",
    borderBottom: "1px solid #111",
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: 700,
  },
  heroImageWrap: {
    position: "relative",
    minHeight: 520,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 360,
      marginTop: theme.spacing(6),
    },
  },
  heroCircle: {
    position: "absolute",
    right: -40,
    bottom: 20,
    width: 260,
    height: 260,
    border: "1px solid #111",
    borderRadius: "50%",
    opacity: 0.65,
    [theme.breakpoints.down("sm")]: {
      width: 180,
      height: 180,
      right: -60,
    },
  },
  heroNumber: {
    position: "absolute",
    top: 20,
    right: 0,
    writingMode: "vertical-rl",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  coatMock: {
    position: "relative",
    width: "70%",
    maxWidth: 360,
    background: "transparent",
    boxShadow: "none",
    transform: "rotate(2deg)",
   
    [theme.breakpoints.down("sm")]: {
      maxWidth: 280,
    },
  },
  coatButton: {
    position: "absolute",
    left: "54%",
    width: 9,
    height: 9,
    borderRadius: "50%",
    background: "#050505",
    border: "1px solid #555",
  },
  heading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.45,
    letterSpacing: "-0.05em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  body: {
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    whiteSpace: "pre-line",
  },
  keywordLine: {
    borderTop: "1px solid #111",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    fontSize: 13,
    color: "#333",
  },
  card: {
    border: "1px solid #111",
    minHeight: 250,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
  },
  cardNumber: {
    fontSize: 24,
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  smallRule: {
    width: 32,
    height: 1,
    background: "#111",
    marginBottom: theme.spacing(3),
  },
  arrowCircle: {
    alignSelf: "flex-end",
    width: 34,
    height: 34,
    border: "1px solid #111",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  comparisonBlock: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  comparisonItem: {
    borderLeft: "1px solid #111",
    paddingLeft: theme.spacing(3),
  },
  bigLetter: {
    fontFamily: '"Times New Roman", serif',
    fontSize: 58,
    lineHeight: 1,
    marginBottom: theme.spacing(1),
  },
  brandGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  brandItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.2, 1.5),
    textAlign: "center",
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 16,
  },
  archiveImages: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1.35fr",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  imagePanel: {
    height: 220,
    background: "#eeeeee",
    position: "relative",
    overflow: "hidden",
  },
  detailPanel: {
    height: 200,
    background: "#eeeeee",
    color: "#111",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontSize: 12,
    letterSpacing: "0.06em",
    lineHeight: 1.8,
  },
  image:{
    display: "block",
    width: "100%",
    height: "auto",
    objectFit: "contain",
  },
  imageCaption: {
    fontSize: 10,
    letterSpacing: "0.12em",
    marginTop: theme.spacing(1),
    textTransform: "uppercase",
  },
  timeline: {
    position: "relative",
    paddingLeft: theme.spacing(5),
    "&:before": {
      content: '""',
      position: "absolute",
      left: 9,
      top: 8,
      bottom: 8,
      width: 1,
      background: "#111",
    },
  },
  timelineItem: {
    position: "relative",
    paddingBottom: theme.spacing(4),
    borderBottom: "1px dashed #aaa",
    marginBottom: theme.spacing(3),
    "&:before": {
      content: '""',
      position: "absolute",
      left: -42,
      top: 4,
      width: 16,
      height: 16,
      border: "1px solid #111",
      borderRadius: "50%",
      background: "#eeeeee",
    },
  },
  timelineNumber: {
    fontSize: 24,
    marginRight: theme.spacing(3),
  },
  philosophyGraphic: {
    position: "relative",
    minHeight: 180,
    [theme.breakpoints.down("sm")]: {
      minHeight: 120,
    },
  },
  circle: {
    position: "absolute",
    right: 78,
    top: 20,
    width: 120,
    height: 120,
    border: "1px solid #111",
    borderRadius: "50%",
  },
  square: {
    position: "absolute",
    right: 0,
    top: 46,
    width: 120,
    height: 120,
    border: "1px solid #111",
  },
  blackDot: {
    position: "absolute",
    right: 82,
    top: 100,
    width: 20,
    height: 20,
    background: "#111",
  },
  finalCta: {
    margin: theme.spacing(8, 0),
    border: "1px solid #111",
    padding: theme.spacing(7, 3),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  finalTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.35,
    letterSpacing: "-0.05em",
    marginBottom: theme.spacing(2),
  },
  stripe: {
    position: "absolute",
    right: 20,
    top: 30,
    bottom: 30,
    width: 48,
    background:
      "repeating-linear-gradient(90deg, transparent 0, transparent 7px, #111 8px, #111 9px)",
    opacity: 0.6,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
}));

const brands = [
  "COMME des GARÇONS",
  "Yohji Yamamoto",
  "JUNYA WATANABE",
  "NOIR KEI NINOMIYA",
  "Maison Margiela",
  "Raf Simons",
  "sacai",
  "ISSEY MIYAKE",
  "PRADA",
  "BALENCIAGA",
  "Rick Owens",
  "UNDERCOVER",
];

const lineUrl = "https://line.me/R/ti/p/@240yzqkh";

function getUtmParams() {
  if (typeof window === "undefined") {
    return {
      source: "direct",
      medium: "",
      campaign: "",
    };
  }

  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get("utm_source") || "direct",
    medium: params.get("utm_medium") || "",
    campaign: params.get("utm_campaign") || "",
  };
}

function trackArchiveLpEvent(eventName, payload = {}) {
  if (typeof window === "undefined" || !window.gtag) return;

  window.gtag("event", eventName, {
    page: "archive_fashion_rental",
    ...getUtmParams(),
    ...payload,
  });
}

export default function ArchiveFashionRentalScreen() {
  const classes = useStyles();
  const title =
    "アーカイブファッションレンタル｜買う前に着て試せるEnclopedia";
  const description =
    "Enclopediaは、COMME des GARÇONS、Yohji Yamamoto、Maison Margielaなどの希少なアーカイブファッションを、買う前にレンタルで着て試せるサービスです。写真や寸法だけでは分からないサイズ感、シルエット、質感を実際に体験できます。";
  const keyword =
    "アーカイブファッションレンタル,アーカイブファッション,ブランド古着レンタル,デザイナーズブランドレンタル,ヴィンテージブランドレンタル,ファッションレンタル,enclopedia,アンクロペディア,買う前に着る,買う前に試せる,コムデギャルソンレンタル,ヨウジヤマモトレンタル,マルジェラレンタル";
  const canonicalUrl =
    `${typeof window !== "undefined" ? window.location.origin : ""}/archive_fashion_rental`;
  const ogImage =
    process.env.REACT_APP_IMAGE_URL
      ? `${process.env.REACT_APP_IMAGE_URL}media/archive_fashion/archive_fashion1.png`
      : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  useEffect(() => {
    headRewrite(title, description, keyword);
    trackArchiveLpEvent("archive_lp_view");
  }, [title, description, keyword]);

  return (
    <div className={classes.root}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keyword} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Enclopedia" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: title,
            description,
            url: canonicalUrl,
            isPartOf: {
              "@type": "WebSite",
              name: "Enclopedia",
              url: typeof window !== "undefined" ? window.location.origin : "",
            },
            about: [
              "アーカイブファッション",
              "ブランド古着レンタル",
              "デザイナーズブランドレンタル",
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: typeof window !== "undefined" ? window.location.origin : "",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "アーカイブファッションレンタル",
                item: canonicalUrl,
              },
            ],
          })}
        </script>
      </Helmet>
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.hero}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow} style={{marginLeft:'7px'}}>ARCHIVE FASHION RENTAL</div>
                <Typography variant="h1" component="h1" className={classes.heroTitle}>
                  アーカイブファッションを、
                  <br />
                  レンタルで気軽に試す。
                </Typography>
                <Typography component="p" className={classes.heroBody}>
                  Enclopediaは、COMME des GARÇONS、Yohji Yamamoto、Maison
                  Margielaなどの希少なアーカイブファッションをレンタル・購入できるサービスです。
                </Typography>
                <div className={classes.buttonRow}>
                  <Button
                    className={`${classes.primaryButton} ${classes.ctaButton}`}
                    component="a"
                    href="https://www.enclopedia-official.com/"
                    onClick={() =>
                      trackArchiveLpEvent("archive_lp_product_list_click", {
                        position: "hero",
                        destination: "home_product_list",
                      })
                    }
                  >
                    レンタルできる商品を見る
                  </Button>
                  <Button
                    className={`${classes.secondaryButton} ${classes.ctaButton}`}
                    component="a"
                    href="/register?source=archive_fashion_rental"
                    onClick={() =>
                      trackArchiveLpEvent("archive_lp_register_click", {
                        position: "hero",
                        destination: "register",
                      })
                    }
                  >
                    無料登録してレンタル予約へ
                  </Button>
                </div>
                <Typography component="p" className={classes.ctaNote}>
                  気になる商品を見つけたら、無料登録でレンタル予約できます。登録料はかかりません。
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.heroImageWrap}>
                  <div className={classes.heroCircle} />
                  <div className={classes.heroNumber}>01 / EXPERIENCE</div>
                  <div className={classes.coatMock}>
                    <img src={process.env.REACT_APP_IMAGE_URL + "media/archive_fashion/archive_fashion1.png"} className={classes.image} alt={'Enclopediaでレンタルできるアーカイブファッションの着用イメージ'}/>
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={5}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>02 / WHY RENT?</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  見るだけでは、服は分からない。
                </Typography>
                <Typography component="p" className={classes.body}>
                  {"アーカイブファッションは、写真やスペックだけでは本当の魅力を理解できません。\n素材の質感、シルエットの立体感、身体とのバランス、時間によって生まれる表情。\nそのすべては、実際に着てみて初めて分かります。"}
                </Typography>
                <div className={classes.keywordLine}>
                  <span>material</span>
                  <span>/</span>
                  <span>silhouette</span>
                  <span>/</span>
                  <span>proportion</span>
                  <span>/</span>
                  <span>time</span>
                </div>
              </Grid>

              <Grid item xs={12} md={7}>
                <div className={classes.labelRow}>
                  <span>03 / WEAR FIRST</span>
                  <span className={classes.labelLine} />
                </div>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <div className={classes.card}>
                      <div>
                        <div className={classes.cardNumber}>01</div>
                        <div className={classes.cardTitle}>RENT</div>
                        <div className={classes.smallRule} />
                        <Typography className={classes.body} component="p">
                          気になるアーカイブをレンタルする
                        </Typography>
                      </div>
                      <div className={classes.arrowCircle}>→</div>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <div className={classes.card}>
                      <div>
                        <div className={classes.cardNumber}>02</div>
                        <div className={classes.cardTitle}>WEAR</div>
                        <div className={classes.smallRule} />
                        <Typography className={classes.body} component="p">
                          実際に着て、サイズ感や雰囲気を確かめる
                        </Typography>
                      </div>
                      <div className={classes.arrowCircle}>→</div>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <div className={classes.card}>
                      <div>
                        <div className={classes.cardNumber}>03</div>
                        <div className={classes.cardTitle}>BUY OR RETURN</div>
                        <div className={classes.smallRule} />
                        <Typography className={classes.body} component="p">
                          気に入れば購入。合わなければ返却。
                        </Typography>
                      </div>
                      <div className={classes.arrowCircle}>→</div>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>04 / COMPARISON</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  ブランド古着通販との違い
                </Typography>
                <div className={classes.comparisonBlock}>
                  <div className={classes.comparisonItem}>
                    <div className={classes.bigLetter}>A</div>
                    <div className={classes.cardTitle}>BUY FIRST</div>
                    <Typography component="p" className={classes.body}>
                      従来のブランド古着通販は、写真と説明文、寸法だけで購入を判断する仕組みです。
                    </Typography>
                  </div>
                  <div className={classes.comparisonItem}>
                    <div className={classes.bigLetter}>B</div>
                    <div className={classes.cardTitle}>WEAR FIRST</div>
                    <Typography component="p" className={classes.body}>
                      Enclopediaは、実際に着てから判断できるので、より納得して選ぶことができます。
                    </Typography>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>05 / BRANDS</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  取り扱いブランド
                </Typography>
                <div className={classes.brandGrid}>
                  {brands.map((brand) => (
                    <div className={classes.brandItem} key={brand}>
                      {brand}
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>06 / ARCHIVE DETAIL</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  服を、資料として見る。
                  <br />
                  そして、実際に着る。
                </Typography>
                <div className={classes.archiveImages}>
                  <div>
                    <div className={classes.imagePanel}>
                      <img src={process.env.REACT_APP_IMAGE_URL + "media/archive_fashion/archive_fashion2.png"} className={classes.image} alt={'アーカイブファッションのフロントディテール'}/>
                    </div>
                    <div className={classes.imageCaption}>FRONT</div>
                  </div>
                  <div>
                    <div className={classes.imagePanel}>
                      <img src={process.env.REACT_APP_IMAGE_URL + "media/archive_fashion/archive_fashion3.png"} className={classes.image} alt={'アーカイブファッションのバックディテール'}/>
                    </div>
                    <div className={classes.imageCaption}>BACK</div>
                  </div>
                  <div>
                    <div className={classes.detailPanel}>
                      <img src={process.env.REACT_APP_IMAGE_URL + "media/archive_fashion/archive_fashion4.png"} className={classes.image} alt={'ブランドアーカイブのラベルディテール'}/>
                    </div>
                    <div className={classes.imageCaption}>LABEL</div>
                    <div style={{ height: 16 }} />
                    <div className={classes.detailPanel}>
                      <img src={process.env.REACT_APP_IMAGE_URL + "media/archive_fashion/archive_fashion5.png"} className={classes.image} alt={'アーカイブファッションの素材とディテール'}/>
                    </div>
                    <div className={classes.imageCaption}>FABRIC / DETAIL</div>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>07 / HOW IT WORKS</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  利用の流れ
                </Typography>
                <div className={classes.timeline}>
                  {[
                    ["01", "アイテムを選ぶ", "気になるアーカイブをラインナップから選びます。"],
                    ["02", "レンタルする", "レンタル期間とプランを選び、注文します。"],
                    ["03", "実際に着る", "実際に着て、サイズ感や雰囲気をじっくり確かめます。"],
                    ["04", "返却または購入", "気に入れば購入。合わなければ返却できます。"],
                  ].map(([num, titleText, text]) => (
                    <div className={classes.timelineItem} key={num}>
                      <Grid container alignItems="flex-start" spacing={2}>
                        <Grid item>
                          <div className={classes.timelineNumber}>{num}</div>
                        </Grid>
                        <Grid item xs>
                          <Typography className={classes.cardTitle} component="h3">
                            {titleText}
                          </Typography>
                          <Typography className={classes.body} component="p">
                            {text}
                          </Typography>
                        </Grid>
                      </Grid>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>08 / PHILOSOPHY</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  アーカイブファッションを、
                  <br />
                  一部の人だけのものにしない。
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography component="p" className={classes.body}>
                  {"アーカイブファッションは、本来もっと多くの人に開かれるべき文化です。\nEnclopediaは、所有を目的とするのではなく、体験し、理解し、未来へつなぐための循環をつくります。\n服が持つ背景や文脈を尊重しながら、次の世代に受け継いでいく。それが、私たちの使命です。"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <div className={classes.philosophyGraphic}>
                  <div className={classes.circle} />
                  <div className={classes.square} />
                  <div className={classes.blackDot} />
                </div>
              </Grid>
            </Grid>
          </section>

          <div className={classes.finalCta}>
            <div className={classes.stripe} />
            <Typography component="h2" className={classes.finalTitle}>
              アーカイブファッションを、買う前に着る。
            </Typography>
            <Typography component="p" className={classes.heroBody} style={{ margin: "0 auto 32px" }}>
              希少なブランドアーカイブをレンタル・購入できます。
            </Typography>
            <div className={classes.buttonRow} style={{ justifyContent: "center" }}>
              <Button
                className={`${classes.primaryButton} ${classes.ctaButton}`}
                component="a"
                href="https://www.enclopedia-official.com/"
                onClick={() =>
                  trackArchiveLpEvent("archive_lp_product_list_click", {
                    position: "final_cta",
                    destination: "home_product_list",
                  })
                }
              >
                商品一覧を見る
              </Button>
              <Button
                className={`${classes.secondaryButton} ${classes.ctaButton}`}
                component="a"
                href="/register?source=archive_fashion_rental"
                onClick={() =>
                  trackArchiveLpEvent("archive_lp_register_click", {
                    position: "final_cta",
                    destination: "register",
                  })
                }
              >
                無料登録する
              </Button>
              <a
                href={lineUrl}
                className={classes.textLink}
                onClick={() =>
                  trackArchiveLpEvent("archive_lp_line_click", {
                    position: "final_cta",
                    destination: "line",
                  })
                }
              >
                LINEで相談する
              </a>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}
